import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowUp, Star, Trash2 } from 'lucide-react';
import type { GalleryImage } from '@/types/gallery';

interface GalleryImageGridProps {
  eventId: string;
  onImagesChanged?: () => void;
}

const GalleryImageGrid = ({ eventId, onImagesChanged }: GalleryImageGridProps) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const getStoragePathFromPublicUrl = (imageUrl: string) => {
    const marker = '/storage/v1/object/public/gallery_images/';
    const markerIndex = imageUrl.indexOf(marker);
    if (markerIndex === -1) return null;

    return decodeURIComponent(
      imageUrl.slice(markerIndex + marker.length).split('?')[0]
    );
  };

  useEffect(() => {
    fetchImages();
  }, [eventId]);

  const fetchImages = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery_images')
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load images',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetCover = async (imageId: string) => {
    try {
      // First, remove cover status from all images
      const { error: resetError } = await supabase
        .from('gallery_images')
        .update({ is_cover: false })
        .eq('event_id', eventId);

      if (resetError) throw resetError;

      // Then set the selected image as cover
      const { error } = await supabase
        .from('gallery_images')
        .update({ is_cover: true })
        .eq('id', imageId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Cover image updated successfully',
      });

      onImagesChanged?.();
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleMoveImage = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;

    const nextImages = [...images];
    [nextImages[index], nextImages[target]] = [nextImages[target], nextImages[index]];

    try {
      const updates = nextImages.map((image, order) =>
        supabase
          .from('gallery_images')
          .update({ display_order: order })
          .eq('id', image.id)
      );

      const results = await Promise.all(updates);
      const failed = results.find((result) => result.error);
      if (failed?.error) throw failed.error;

      setImages(nextImages.map((image, order) => ({ ...image, display_order: order })));
      onImagesChanged?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (image: GalleryImage) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      const storagePath = getStoragePathFromPublicUrl(image.image_url);
      if (storagePath) {
        const { error: storageError } = await supabase.storage
          .from('gallery_images')
          .remove([storagePath]);

        if (storageError) throw storageError;
      }

      const { error } = await supabase
        .from('gallery_images')
        .delete()
        .eq('id', image.id);

      if (error) throw error;

      const remainingImages = images.filter((item) => item.id !== image.id);
      if (image.is_cover && remainingImages.length > 0) {
        const { error: coverError } = await supabase
          .from('gallery_images')
          .update({ is_cover: true })
          .eq('id', remainingImages[0].id);

        if (coverError) throw coverError;
      }

      toast({
        title: 'Success',
        description: 'Image deleted successfully',
      });

      onImagesChanged?.();
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div>Loading images...</div>;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {images.map((image) => (
        <div key={image.id} className="relative group">
          <img
            src={image.image_url}
            alt={image.alt_text || 'Gallery image'}
            className="w-full h-48 object-cover rounded-lg"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center space-x-2">
            <Button
              size="icon"
              variant="secondary"
              onClick={() => handleMoveImage(images.indexOf(image), -1)}
              disabled={images.indexOf(image) === 0}
              title="Move earlier"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => handleMoveImage(images.indexOf(image), 1)}
              disabled={images.indexOf(image) === images.length - 1}
              title="Move later"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant={image.is_cover ? "default" : "secondary"}
              onClick={() => handleSetCover(image.id)}
              title={image.is_cover ? "Current cover image" : "Set as cover"}
            >
              <Star className={`h-4 w-4 ${image.is_cover ? "text-yellow-400" : ""}`} />
            </Button>
            <Button
              size="icon"
              variant="destructive"
              onClick={() => handleDelete(image)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {image.is_cover && (
            <div className="absolute top-2 right-2 bg-yellow-400 text-black text-xs px-2 py-1 rounded">
              Cover
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default GalleryImageGrid;